<template>
  <div>
    <div class="d-flex">
      <div class="seat-map-header flex-grow d-flex flex-row flex-lg-column flex-xl-column">
        <div class="bold d-flex gap-5px">
          <span v-html="svgSeatChoice(18)" class="icon"></span>
          <span class="font-size-20px">Scelta del posto</span>
        </div>
        <div class="d-none d-lg-block d-xl-block">
          <span v-if="isEditing" class="d-flex flex-column gap-5px">
            <span class="d-flex gap-8px">
              <span class="thin font-size-14px"
                >Volo {{ currentSegmentIndex + 1 }} / {{ segments.length }}:</span
              >
              <span v-if="segments[currentSegmentIndex]" class="font-size-16px"
                >{{ locations[segments[currentSegmentIndex].departure.iataCode].name }} -
                {{ locations[segments[currentSegmentIndex].arrival.iataCode].name }}</span
              >
            </span>
            <span class="d-none d-lg-block d-xl-block mb-2 ont-size-14px"
              >Selezione del posto per
              <span class="text-primary bold">{{
                formatPassengerName(sittingPassengers[currentPassengerIndex])
              }}</span>
              <span v-if="isPassengerHoldingAnInfant">
                con <span class="text-primary bold">neonato</span></span
              ></span
            >
          </span>
          <span v-else>Selezionando i posti che preferisci ti godrai un viaggio più comodo.</span>
        </div>
        <div class="d-block d-lg-none d-xl-none">
          <span v-if="isEditing"
            >{{ currentItinerary.segments[0].departure.iataCode }} -
            {{
              currentItinerary.segments[currentItinerary.segments.length - 1].arrival.iataCode
            }}</span
          >
        </div>
      </div>
      <b-button
        v-if="!isEditing"
        class="d-flex pt-2"
        variant="ghost"
        size="sm"
        role="button"
        @click="() => changeSeatSelection()"
      >
        <span v-html="svgChange(14)" class="mr-1"></span
        ><span class="text-decoration-underline font-size-14px">Modifica</span>
      </b-button>
    </div>
    <div v-if="isEditing" class="seat-map-editor">
      <div class="d-flex flex-xl-row flex-lg-row flex-md-column flex-sm-column flex-column gap-16">
        <!-- Navigation between segments and passengers -->
        <div class="d-flex flex-column flex-basis-30 seat-map-side-bar">
          <div class="d-none flex-grow d-lg-block d-xl-block" v-if="showLegend['desktop']">
            <seat-map-info :list="seatsLegend" :onHide="() => toggleShowLegend('desktop')" />
          </div>
          <seat-map-navigation
            v-else
            :disabled="showLegend['mobile']"
            :segments="segments"
            :currentSegmentIndex="currentSegmentIndex"
            :currentSegmentId="currentSegmentId"
            :setCurrentSegmentId="(v) => setCurrentSegmentId(v)"
            :availableDecks="availableDecks"
            :currentDeckIndex="String(currentDeckIndex)"
            :setCurrentDeckIndex="(v) => setCurrentDeckIndex(v)"
            :sittingPassengers="sittingPassengers"
            :currentPassengerId="currentPassengerId"
            :setCurrentPassengerId="(v) => setCurrentPassengerId(v)"
            :parsedSelections="parsedSelections"
            :resetSegmentSelections="(segmentId) => resetSegmentSelections(segmentId)"
          />
          <seat-map-info-button
            :showOnMobile="false"
            :toggled="showLegend['desktop']"
            :toggle="() => toggleShowLegend('desktop')"
          />
        </div>
        <!-- Seats configuration -->
        <div class="seat-map-main flex-basis-70">
          <div class="overflow-hidden position-relative">
            <div class="seat-map-container panel position-relative">
              <deck
                v-if="isCurrentDeckEnabled"
                :currentTravelerChoosing="currentPassengerId"
                :deckConfiguration="deckConfiguration"
                :mappedDeck="mappedDeck"
                :selectedSeats="selectedSeats"
                :handleSeatSelection="
                  (seat) => {
                    const success = handleSeatSelection(currentSegmentId, currentPassengerId, seat);
                    if (success) {
                      nextPassenger(seat);
                    }
                  }
                "
              />
              <div
                v-else
                class="d-flex flex-column gap-5px justify-content-center h-100 m-auto my-4 p-4"
              >
                <div class="bold">Non è possibile scegliere i posti</div>
                <div>
                  La compagnia non permette la selezione in questa fase e assegnerà automaticamente
                  i posti. La compagnia non garantisce che i passeggeri adulti viaggino vicini.
                </div>
                <div>
                  Normalmente, a seconda delle regole della compagnia, potrai scegliere i posti
                  durante il check-in online o in aeroporto.
                </div>
              </div>
            </div>
            <div class="infant-notice-container w-100 h-100">
              <div
                v-if="showLegend['mobile']"
                class="seat-map-info-container d-block position-absolute d-lg-none d-xl-none flex-grow w-100 h-100 pointer-events-all"
              >
                <seat-map-info :list="seatsLegend" :onHide="() => toggleShowLegend('mobile')" />
              </div>
              <div
                v-else-if="isPassengerHoldingAnInfant && isCurrentDeckEnabled"
                class="d-none d-lg-flex d-xl-flex infant-notice pointer-events-all shadow-lg"
                v-b-popover.hover.right="'Stai selezionando un posto condiviso con un neonato.'"
              >
                <span v-html="svg_infant()" class="d-block"></span>
              </div>
            </div>
          </div>

          <div class="p-3">
            <seat-map-info-button
              showOnMobile
              :toggled="showLegend['mobile']"
              :toggle="() => toggleShowLegend('mobile')"
            />
          </div>
          <seat-map-footer
            :mustSelectSeats="mustSelectSeats"
            :isPreviousActionVisible="segments.length > 1"
            :isPreviousActionDisabled="currentSegmentIndex === 0 || showLegend['mobile']"
            :previousSegmentAction="() => previousSegment()"
            :nextSegmentActionLabel="isLastSegmentSelected ? 'Conferma' : 'Prossimo volo'"
            :isNextSegmentActionDisabled="isNextSegmentActionDisabled"
            :handleNextSegmentAction="() => handleNextSegmentAction()"
            :parsedSelections="parsedSelections"
          />
        </div>
      </div>
      <div
        v-if="!mustSelectSeats"
        class="seat-map-skip border-top pt-3"
        @click="() => skipSeatChoice()"
        style="cursor: pointer"
      >
        <input type="radio" />
        <div class="font-size-16px">
          <div class="bold">No grazie, non voglio scegliere il posto a sedere</div>
          <div>
            Se salti la selezione ora, i posti verranno assegnati dalla compagnia. La compagnia non
            garantisce che gli adulti siano seduti vicini. Alcune compagnie permettono di nuovo
            questa scelta al check-in online o in aeroporto.
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <seat-summary
        :locations="locations"
        :itineraries="itineraries"
        :passengers="passengers"
        :selections="parsedSelections"
      />
      <b-button
        v-if="!isEditing"
        class="d-flex ml-auto border"
        variant="outline"
        size="md"
        role="button"
        @click="() => changeSeatSelection()"
      >
        <span v-html="svgChange(14)" class="mr-1"></span
        ><span class="text-decoration-underline font-size-14px">Modifica</span>
      </b-button>
      <!-- Warning Notice -->
      <hr />
      <div class="seat-map-skip">
        <span v-html="svgInfo()"></span>
        <span class="font-size-16px">
          La compagnia aerea non garantisce l’attribuzione dei posti da te selezionati e si riserva
          il diritto di riassegnartene altri con caratteristiche analoghe senza alcun costo
          aggiuntivo.
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { seatCharacteristicsChecks, seatsLegend } from '@/resources/seat-map';
import { formatPassengerName, formatSegmentLabel } from '@/utilities';
import { svgSeatChoice, svgInfo, svgChange } from '@/components/svg/seat-map';
import Deck from '@/components/blocks/seat-map/deck.vue';
import Summary from '@/components/blocks/seat-map/seat-summary.vue';
import SeatMapInfo from '@/components/blocks/seat-map/seat-map-info.vue';
import SeatMapInfoButton from '@/components/blocks/seat-map/seat-map-info-button.vue';
import SeatMapNavigation from '@/components/blocks/seat-map/seat-map-navigation.vue';
import SeatMapFooter from '@/components/blocks/seat-map/seat-map-footer.vue';
import { filterHeldPassengers, sortPassengers } from '../../../utilities/seat-map';
import { infant } from '../../svg/user';

export default {
  name: 'seat-map',
  components: {
    'seat-map-info-button': SeatMapInfoButton,
    'seat-map-navigation': SeatMapNavigation,
    'seat-map-footer': SeatMapFooter,
    'seat-map-info': SeatMapInfo,
    deck: Deck,
    'seat-summary': Summary,
  },
  props: {
    mustSelectSeats: Boolean,
    mappedDecks: Object,
    passengers: Array,
    selections: String,
    handleSeatSelection: Function,
    resetSeatSelection: Function,
    updateParentWithCurrentStatus: Function,
    resetSegmentSelections: Function,
  },
  data() {
    return {
      isEditing: true,
      currentSegmentId: '',
      currentPassengerId: '',
      currentDeckIndex: 0,
      showLegend: {
        mobile: false,
        desktop: false,
      },
      seatsLegend,
      svg_infant: infant,
      svgSeatChoice,
      svgInfo,
      svgChange,
    };
  },
  mounted() {
    this.selectFirstSegment(this.segments);
    if (this.availableDecks.length) {
      const firstAvailableDeck = this.availableDecks.findIndex((deck) => !deck.disabled);
      this.currentDeckIndex = Math.max(0, firstAvailableDeck);
    }
    const _this = this;
    window.eventBus.$on('seat_map_next_step', () => {
      _this.handleNextSegmentAction();
    });
    this.updateParentStatus(
      this.isEditing,
      this.isLastSegmentSelected,
      this.isNextSegmentActionDisabled
    );
  },
  computed: {
    locations() {
      return this.$store.state.liveflight.results.dictionaries.locations;
    },
    itineraries() {
      return this.$store.state.liveflight.flight_offer.itineraries;
    },
    currentItinerary() {
      const itineraryIndex = this.itineraries.findIndex((itinerary) =>
        itinerary.segments.some((segment) => segment.id === this.currentSegmentId)
      );
      return this.itineraries[Math.max(0, itineraryIndex)];
    },
    currentSegmentIndex() {
      return this.segments.findIndex((d) => d.segmentId === this.currentSegmentId);
    },
    currentPassengerIndex() {
      return this.getPassengerIndex(this.currentPassengerId);
    },
    isLastSegmentSelected() {
      return this.currentSegmentIndex === this.segments.length - 1;
    },
    isPassengerHoldingAnInfant() {
      return (
        this.sittingPassengers[this.currentPassengerIndex] &&
        this.sittingPassengers[this.currentPassengerIndex].holding
      );
    },
    currentSegment() {
      return this.segments[Math.max(0, this.currentSegmentIndex)];
    },
    availableDecks() {
      if (this.currentSegment && this.currentSegment.decks) {
        return this.currentSegment.decks.map((d, index) => ({
          id: index,
          deckType: d.deckType,
          value: index,
          label: d.deckType,
        }));
      }
      return [];
    },
    isCurrentDeckEnabled() {
      if (process && process.env) {
        return !this.mappedDeck.disabled || process.env.VUE_APP_SHOW_DISABLED_SEAT_MAP;
      }
      return !this.mappedDeck.disabled;
    },
    currentDeck() {
      return this.currentSegment.decks[this.currentDeckIndex];
    },
    deckConfiguration() {
      if (this.currentDeck) return this.currentDeck.deckConfiguration || {};
      return {};
    },
    mappedDeck() {
      const currentSegmentDecks = this.mappedDecks[this.currentSegmentId];
      if (!currentSegmentDecks) {
        return {};
      }
      return currentSegmentDecks.decks[this.currentDeckIndex]
        ? currentSegmentDecks.decks[this.currentDeckIndex]
        : {};
    },
    segments() {
      return Object.values(this.mappedDecks).map((obj) => obj.segment);
    },
    parsedSelections() {
      try {
        const selections = JSON.parse(this.selections);
        return selections;
      } catch (e) {
        this.log('parsedSeatSelections error');
        this.log(e);
        return {};
      }
    },
    selectedSeats() {
      const mappedSeats = {};

      if (!this.currentSegmentId) {
        return mappedSeats;
      }

      const passengerSeats = this.parsedSelections[this.currentSegmentId];
      if (!passengerSeats) {
        return mappedSeats;
      }
      this.passengers.forEach((p) => {
        const seat = passengerSeats[p.id];
        if (seat) {
          mappedSeats[seat.number] = p;
        }
      });
      return mappedSeats;
    },
    sittingPassengers() {
      const sittingPassengers = this.filterHeldPassengers(this.passengers);
      return this.sortPassengers(sittingPassengers);
    },
    isNextSegmentActionDisabled() {
      if (this.showLegend['mobile']) {
        return true;
      }
      if (!this.mustSelectSeats || !this.isLastSegmentSelected) {
        return false;
      }
      const segmentSelections = Object.values(this.parsedSelections);
      if (!segmentSelections || segmentSelections.length < this.segments.length) {
        return true;
      }
      for (let index = 0; index < segmentSelections.length; index += 1) {
        if (Object.keys(segmentSelections[index]).length < this.sittingPassengers.length) {
          return true;
        }
      }
      return false;
    },
  },
  created() {
    window.addEventListener('resize', this.onResize);
  },
  destroyed() {
    window.removeEventListener('resize', this.onResize);
  },
  methods: {
    formatPassengerName,
    formatSegmentLabel,
    filterHeldPassengers,
    sortPassengers,
    onResize() {
      if (this.isEditing) {
        this.showLegend = {
          mobile: false,
          desktop: false,
        };
      }
    },
    scrollTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    },
    selectFirstSegment(segments) {
      if (segments.length) {
        this.setCurrentSegmentId(segments[0].segmentId);
      }
    },
    changeSeatSelection() {
      this.selectFirstSegment(this.segments);
      this.isEditing = true;
    },
    getPassengerIndex(passengerId) {
      return this.sittingPassengers.findIndex((d) => d.id === passengerId);
    },
    setCurrentPassengerId(newPassengerId) {
      this.currentPassengerId = newPassengerId;
    },
    setCurrentDeckIndex(newDeckIndex) {
      this.currentDeckIndex = Math.min(this.availableDecks.length - 1, Math.max(0, newDeckIndex));
    },
    setCurrentSegmentId(newSegmentId) {
      this.currentSegmentId = newSegmentId;
      this.setCurrentPassengerId(this.sittingPassengers[0].id);
      this.setCurrentDeckIndex(0);
      this.scrollTop();
    },
    nextPassenger(seat) {
      // Next available index
      let nextIndex = this.currentPassengerIndex + 1;

      // Check if someone was already in that seat
      if (seat && this.selectedSeats[seat.number]) {
        nextIndex = this.getPassengerIndex(this.selectedSeats[seat.number].id);
      }

      if (nextIndex < this.sittingPassengers.length) {
        this.setCurrentPassengerId(this.sittingPassengers[nextIndex].id);
      }
    },
    moveToSegment(direction) {
      const nextIndex = this.currentSegmentIndex + direction;
      const nextSegment = this.segments[Math.min(Math.max(nextIndex, 0), this.segments.length - 1)];
      this.setCurrentSegmentId(nextSegment.segmentId);
    },
    nextSegment() {
      this.moveToSegment(1);
    },
    previousSegment() {
      this.moveToSegment(-1);
    },
    handleNextSegmentAction() {
      // Sanity check since it could be triggered from parent
      if (this.isNextSegmentActionDisabled || !this.isEditing) {
        return;
      }

      if (this.isLastSegmentSelected) {
        this.isEditing = false;
        return;
      }

      this.nextSegment();
    },
    filterAvailabilityBasedOnPassenger(seats, passenger) {
      // additional checks for children and infants
      return seats.map((seat) => {
        if (!passenger) {
          return seat;
        }
        const isNotAvailableForPassenger = seat.characteristicsCodes.some(
          (code) =>
            seatCharacteristicsChecks[code] &&
            !seatCharacteristicsChecks[code].selectable(passenger)
        );
        if (isNotAvailableForPassenger) {
          return {
            ...seat,
            travelerPricing: seat.travelerPricing.map((pricing) => ({
              ...pricing,
              seatAvailabilityStatus: 'BLOCKED',
            })),
          };
        }
        return seat;
      });
    },
    skipSeatChoice() {
      this.resetSeatSelection();
      this.isEditing = false;
    },
    toggleShowLegend(device) {
      this.showLegend = {
        ...Object.fromEntries(
          Object.entries(this.showLegend).map((entry) => {
            const key = entry[0];
            return [key, key === device ? !entry[1] : false];
          })
        ),
      };
    },
    updateParentStatus(isEditing, isLastSegmentSelected, btnDisabled) {
      let btnText = 'Continua';
      if (isEditing) {
        btnText = isLastSegmentSelected ? 'Conferma' : 'Prossimo volo';
      }
      this.updateParentWithCurrentStatus({ isEditing, btnText, btnDisabled });
    },
  },
  watch: {
    isEditing: {
      handler(newValue, oldValue) {
        (newValue, oldValue);
        const _this = this;
        this.$nextTick(() => {
          _this.scrollTop();
          _this.updateParentStatus(
            newValue,
            _this.isLastSegmentSelected,
            _this.isNextSegmentActionDisabled
          );
        });
      },
    },
    isLastSegmentSelected: {
      handler(newValue, oldValue) {
        (newValue, oldValue);
        const _this = this;
        if (this.isEditing) {
          this.$nextTick(() => {
            _this.updateParentStatus(_this.isEditing, newValue, _this.isNextSegmentActionDisabled);
          });
        }
      },
    },
    isNextSegmentActionDisabled: {
      handler(newValue, oldValue) {
        (newValue, oldValue);
        const _this = this;
        this.$nextTick(() => {
          _this.updateParentStatus(_this.isEditing, _this.isLastSegmentSelected, newValue);
        });
      },
    },
  },
};
</script>

<style lang="scss">
.flex-grow {
  flex: 1;
}

.seat-map-legend {
  text-align: right;
}

.gap-16 {
  gap: 16px;
}

.seat-map-editor {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.seat-map-header {
  padding: 1em 0;
  gap: 0.5em;
}

.seat-map-container {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-around;

  min-height: 50em;
  max-height: 50em;

  overflow-y: scroll;
  overflow-x: scroll;

  padding: 0 !important;
  background-color: $light-gray-blue !important;
}

.seat-map-skip {
  display: flex;
  flex-direction: row;
  align-items: baseline;

  justify-content: center;
  gap: 1em;
}
.carrier-logo {
  max-height: 30px;
}

.segment-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.text-decoration-underline {
  text-decoration: underline;
}

.infant-notice {
  pointer-events: all;
  position: absolute;
  bottom: 40px;
  left: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 100%;
  max-width: 40px;
  min-width: 40px;

  max-height: 40px;
  min-height: 40px;
  background-color: white;
}

.infant-notice-container {
  position: absolute;
  top: 0;
  pointer-events: none;
}

.pointer-events-all {
  pointer-events: all;
}

.seat-map-info-container {
  background-color: white;
}
</style>
